import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api, reviewKey, setReviewKey, type Allowance, type ReportSummary, type User } from './api';
import { isNative, syncPurchases } from './native';
import { AuthProvider, type Auth } from './auth';
import { SignIn } from './pages/SignIn';
import { Onboarding } from './pages/Onboarding';
import { Search } from './pages/Search';
import { BuyerFit } from './pages/BuyerFit';
import { Assembling } from './pages/Assembling';
import { Report } from './pages/Report';
import { ModuleDetail } from './pages/ModuleDetail';
import { Portfolio } from './pages/Portfolio';
import { Account } from './pages/Account';
import { AcceptTerms, Agreement } from './pages/Agreement';
import { c } from './theme';
import { trackView } from './track';

export function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routed />
    </BrowserRouter>
  );
}

/**
 * Every new screen opens at the top. A single-page app keeps the window's
 * scroll position across route changes, so CONTINUE from halfway down one
 * screen landed halfway down the next.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    trackView(pathname);
  }, [pathname]);
  return null;
}

function Routed() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  // ?review=<key> switches this browser into review mode; the key leaves the address bar at once.
  const [reviewing, setReviewing] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    const k = q.get('review');
    if (k) {
      setReviewKey(k);
      q.delete('review');
      const rest = q.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
    return !!reviewKey();
  });

  const signOut = useCallback<Auth['signOut']>(async () => {
    // Clear the cookie first, then the local state, then land on the welcome
    // screen. Dropping any one of the three leaves the app half signed-in.
    await api.signOut().catch(() => undefined);
    setUser(null);
    setReports([]);
    navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    api
      .me()
      .then(({ user: u }) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setReady(true));
  }, []);

  const refreshReports = useCallback(() => {
    if (!user) {
      setReports([]);
      setLoadingReports(false);
      return;
    }
    setLoadingReports(true);
    api
      .reports()
      .then(({ reports: rs }) => setReports(rs))
      .catch(() => setReports([]))
      .finally(() => setLoadingReports(false));
  }, [user]);

  // A report bought in the app adds to the allowance the moment it is credited.
  const setAllowance = useCallback((allowance: Allowance) => setUser((u) => (u ? { ...u, allowance } : u)), []);

  // In the iOS app, purchases StoreKit has not seen credited yet (a dropped
  // connection, Ask to Buy) are sent again once someone is signed in.
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) return;
    return syncPurchases(api.storePurchase, (r) => setAllowance(r.allowance));
  }, [userId, setAllowance]);

  // The report list backs the menu on every screen, so it refreshes on navigation.
  useEffect(refreshReports, [refreshReports, location.pathname]);

  if (!ready) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: c.page,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          letterSpacing: '0.2em',
          fontWeight: 600,
          color: c.ink45,
        }}
      >
        READYBUY
      </div>
    );
  }

  // Review mode: the team reading a tester's report. No sign-in or agreement gate; read only.
  if (reviewing && location.pathname.startsWith('/report/')) {
    const exit = () => {
      setReviewKey(null);
      setReviewing(false);
      navigate('/', { replace: true });
    };
    return (
      <AuthProvider value={{ user, signOut }}>
        <Routes>
          <Route path="/report/:id" element={<Report user={user} reports={reports} />} />
          <Route path="/report/:id/:moduleKey" element={<ModuleDetail user={user} reports={reports} />} />
        </Routes>
        <button
          type="button"
          onClick={exit}
          className="rb-review-pill"
          style={{
            position: 'fixed',
            left: 12,
            bottom: 12,
            zIndex: 9999,
            border: 0,
            borderRadius: 999,
            padding: '9px 14px',
            background: c.ink,
            color: '#fff',
            fontSize: 10,
            letterSpacing: '0.16em',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          REVIEW MODE · EXIT
        </button>
      </AuthProvider>
    );
  }

  if (!user) {
    return (
      <AuthProvider value={{ user, signOut }}>
        <Routes>
        <Route path="/" element={<Onboarding />} />
        <Route path="/signin" element={<SignIn onUser={setUser} />} />
        <Route path="/signup" element={<SignIn onUser={setUser} initialMode="signup" />} />
        {/* A signed-out visitor lands on the welcome screen, wherever they aimed. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    );
  }

  // Signed in, but the testing agreement (the App Store app: the terms of use) is not agreed yet: nothing else until it is.
  if (!user.agreementSigned) {
    return (
      <AuthProvider value={{ user, signOut }}>
        <Routes>
          <Route
            path="*"
            element={
              isNative ? (
                <AcceptTerms onAccepted={() => setUser({ ...user, agreementSigned: true })} />
              ) : (
                <Agreement user={user} onSigned={() => setUser({ ...user, agreementSigned: true })} />
              )
            }
          />
        </Routes>
      </AuthProvider>
    );
  }

  return (
    <AuthProvider value={{ user, signOut }}>
      <Routes>
      <Route path="/agreement" element={<Agreement user={user} />} />
      <Route path="/" element={<Navigate to="/search" replace />} />
      <Route path="/signin" element={<Navigate to="/search" replace />} />
      <Route path="/signup" element={<Navigate to="/search" replace />} />
      <Route path="/search" element={<Search user={user} reports={reports} onAllowance={setAllowance} />} />
      <Route path="/fit" element={<BuyerFit user={user} reports={reports} />} />
      <Route
        path="/portfolio"
        element={<Portfolio user={user} reports={reports} loading={loadingReports} onChanged={refreshReports} />}
      />
      <Route path="/account" element={<Account user={user} reports={reports} />} />
      <Route path="/assembling/:id" element={<Assembling user={user} reports={reports} />} />
      <Route path="/report/:id" element={<Report user={user} reports={reports} />} />
      <Route path="/report/:id/:moduleKey" element={<ModuleDetail user={user} reports={reports} />} />
        <Route path="*" element={<Navigate to="/search" replace />} />
      </Routes>
    </AuthProvider>
  );
}
